/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { AxiosError, AxiosResponse } from "axios";
import {
    postHandler,
    putHandler,
    customErrHandlerFactory,
    stdErrHandlerFactory,
    getHandlerVersionCaching,
} from "../../utils/Requests";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import {
    Blank,
    EditWorkOrderRequest,
    Effect,
    ExpandedWorkOrder,
    OperationFailed,
    Reference,
    Setter,
    VersionedPagination,
    WorkOrder,
    WorkOrderIngestionResponse,
} from "../../utils/Interfaces";
import { UploadStateProps } from "./ImportWalmartWorkOrderModal";
import { setExistingWorkOrders } from "./workOrdersSlice";
import { store } from "../../app/store";

function handleWorkOrderUploadResponse(
    response: AxiosResponse<WorkOrderIngestionResponse>,
    setUploadState: Setter<UploadStateProps>,
    setWorkOrderLink: Setter<string>
) {
    let message: string = "Work order imported successfully.";
    let type: "success" | "error" | "warning" | "info" = "success";
    let title: string = "Upload Succeeded";

    if (response.data.error_fields.length > 0) {
        message =
            "The following fields could not be imported automatically and need manual review: " +
            response.data.error_fields.join(", ") +
            ".";
        type = "warning";
        title = "Upload Succeeded with Warnings";
    }

    setUploadState({
        type: type,
        message: message,
        title: title,
    });

    if (response.data.work_order) {
        setWorkOrderLink(response.data.work_order);
    }
}

export function uploadPDFWorkOrder(
    form: FormData,
    file: File,
    dispatch: Dispatch<AnyAction>,
    setUploadState: Setter<UploadStateProps>,
    setWorkOrderLink: Setter<string>
) {
    form.append("upload_file", file, file.name);

    putHandler<WorkOrderIngestionResponse>(
        "/api/work-orders/upload/file",
        form,
        { "Content-Type": "application/pdf" },
        dispatch,
        (response: AxiosResponse<WorkOrderIngestionResponse>): void => {
            handleWorkOrderUploadResponse(response, setUploadState, setWorkOrderLink);
        },
        customErrHandlerFactory(dispatch, (error: AxiosError<OperationFailed, any>): void => {
            if (!error.response || error.response.status === 400) {
                setUploadState({
                    type: "error",
                    title: "Please check the following errors",
                    message: error.response?.data.error ?? "An unidentified error has occurred.",
                });
            }
        }),
        (value: boolean) => {}
    );
}

export function uploadTextWorkOrder(
    text: string,
    dispatch: Dispatch<AnyAction>,
    setUploadState: Setter<UploadStateProps>,
    setWorkOrderLink: Setter<string>
) {
    postHandler<{ text: string }, WorkOrderIngestionResponse>(
        "/api/work-orders/upload/text",
        { text: text },
        dispatch,
        (response: AxiosResponse<WorkOrderIngestionResponse, any>): void => {
            handleWorkOrderUploadResponse(response, setUploadState, setWorkOrderLink);
        },
        customErrHandlerFactory(dispatch, (error: AxiosError<OperationFailed, any>): void => {
            if (!error.response || error.response.status === 400) {
                setUploadState({
                    type: "error",
                    title: "Please check the following errors",
                    message: error.response?.data.error ?? "An unidentified error has occurred.",
                });
            }
        }),
        (loading: boolean): void => {}
    );
}

export function createWorkOrder(workOrder: WorkOrder, dispatch: Dispatch<AnyAction>, onWorkOrderCreated: Effect): void {
    let workOrderConverted: EditWorkOrderRequest = {
        ticket_no: workOrder.ticket_no,
        manager_name: workOrder.manager_name ?? "",
        date_received: workOrder.date_received ?? null,
        problem_description: workOrder.problem_description ?? "",
        additional_description: workOrder.additional_description ?? "",
        device_to_be_fixed: workOrder.device_to_be_fixed ?? "",
        scissor_lift_available: workOrder.scissor_lift_available ?? false,
        comments: workOrder.comments ?? "",
    };

    postHandler<Blank, Reference>(
        `/api/work-orders/create/${workOrder.site.id}`,
        workOrderConverted,
        dispatch,
        () => {
            updateWorkOrders(dispatch, () => {});
            onWorkOrderCreated();
        },
        stdErrHandlerFactory(dispatch),
        (loading: boolean) => {}
    );
}

export function updateWorkOrders(dispatch: Dispatch<AnyAction>, setLoading: Setter<boolean>) {
    getHandlerVersionCaching<ExpandedWorkOrder>(
        "/api/work-orders/list-open",
        store.getState().workOrders.existingWorkOrdersVersion,
        dispatch,
        (response: AxiosResponse<VersionedPagination<ExpandedWorkOrder>, any>) => {
            dispatch(setExistingWorkOrders(response.data));
        },
        () => {},
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}
